import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import './FileDetailsScreen.css';

export default function FileDetailsScreen() {
  const { customerId, fileId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchFileData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch customers list to find the specific customer
        const customers = await api.getCustomers();
        const foundCustomer = customers.find(c => c.id === customerId);
        
        if (!foundCustomer) {
          setError('Customer not found');
          return;
        }

        setCustomer(foundCustomer);

        // Fetch customer files
        const customerFiles = await api.getCustomerFiles(customerId);
        const foundFile = customerFiles.find(f => f.id === fileId);
        
        if (!foundFile) {
          setError('File not found');
          return;
        }

        setFile(foundFile);
      } catch (err) {
        console.error('Failed to fetch file data:', err);
        setError(err.message || 'Failed to load file data');
      } finally {
        setIsLoading(false);
      }
    };

    if (customerId && fileId) {
      fetchFileData();
    }
  }, [customerId, fileId]);

  const formatUuid = (uuid) => {
    if (!uuid) return '—';
    const parts = uuid.split('-');
    return parts[0] || '—';
  };

  const getFileTypeLabel = (type) => {
    const typeMap = {
      'UTILITY_BILL': 'Utility Bill',
      'PASSPORT': 'Passport',
    };
    return typeMap[type] || type || 'Unknown';
  };

  const isImageFile = (fileType) => {
    return fileType && fileType.startsWith('image/');
  };

  const isPdfFile = (fileType) => {
    return fileType && (fileType === 'application/pdf' || fileType.includes('pdf'));
  };

  if (isLoading) {
    return (
      <div className="vt-fileDetails">
        <div className="vt-pageHead">
          <button 
            className="vt-btn vt-btn--secondary" 
            onClick={() => navigate(`/customers/${customerId}`)}
            style={{ marginBottom: '1rem' }}
          >
            ← Back to Customer
          </button>
          <h1 className="vt-pageTitle">Loading file...</h1>
        </div>
      </div>
    );
  }

  if (error || !file) {
    return (
      <div className="vt-fileDetails">
        <div className="vt-pageHead">
          <button 
            className="vt-btn vt-btn--secondary" 
            onClick={() => navigate(`/customers/${customerId}`)}
            style={{ marginBottom: '1rem' }}
          >
            ← Back to Customer
          </button>
          <h1 className="vt-pageTitle">File Details</h1>
        </div>
        <div className="vt-surface" style={{ padding: '2rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--vt-danger)' }}>{error || 'File not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vt-fileDetails">
      <div className="vt-pageHead">
        <button 
          className="vt-btn vt-btn--secondary" 
          onClick={() => navigate(`/customers/${customerId}`)}
          style={{ marginBottom: '1rem' }}
        >
          ← Back to Customer
        </button>
        <h1 className="vt-pageTitle">{getFileTypeLabel(file.type)}</h1>
        <p className="vt-pageSub vt-muted">
          File ID: {formatUuid(file.id)}
        </p>
      </div>

      <div className="vt-fileDetailsGrid">
        <section className="vt-fileViewer vt-surface">
          <h2 className="vt-sectionTitle">Document Preview</h2>
          <div className="vt-fileViewerContent">
            {file.secureUrl && isImageFile(file.fileType) ? (
              <div className="vt-imageViewer">
                {!imageError ? (
                  <img 
                    src={file.secureUrl} 
                    alt={getFileTypeLabel(file.type)}
                    className="vt-documentImage"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="vt-viewerError">
                    <p>Failed to load image</p>
                    <a 
                      href={file.secureUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="vt-btn vt-btn--primary"
                    >
                      Open in new tab
                    </a>
                  </div>
                )}
              </div>
            ) : file.secureUrl && isPdfFile(file.fileType) ? (
              <div className="vt-pdfViewer">
                <iframe
                  src={file.secureUrl}
                  className="vt-pdfFrame"
                  title={getFileTypeLabel(file.type)}
                />
              </div>
            ) : file.secureUrl ? (
              <div className="vt-otherFileViewer">
                <div className="vt-viewerPlaceholder">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  <p className="vt-muted">Preview not available for this file type</p>
                  <a 
                    href={file.secureUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="vt-btn vt-btn--primary"
                    style={{ marginTop: '1rem' }}
                  >
                    Download File
                  </a>
                </div>
              </div>
            ) : (
              <div className="vt-viewerError">
                <p className="vt-muted">File URL not available</p>
              </div>
            )}
          </div>
        </section>

        <section className="vt-fileInfo vt-surface">
          <h2 className="vt-sectionTitle">File Information</h2>
          <dl className="vt-detailsList">
            <div className="vt-detailsItem">
              <dt className="vt-detailsLabel">Type</dt>
              <dd className="vt-detailsValue">{getFileTypeLabel(file.type)}</dd>
            </div>
            <div className="vt-detailsItem">
              <dt className="vt-detailsLabel">File Type</dt>
              <dd className="vt-detailsValue">{file.fileType || '—'}</dd>
            </div>
            <div className="vt-detailsItem">
              <dt className="vt-detailsLabel">File ID</dt>
              <dd className="vt-detailsValue">{formatUuid(file.id)}</dd>
            </div>
            {file.secureUrl && (
              <div className="vt-detailsItem">
                <dt className="vt-detailsLabel">Actions</dt>
                <dd className="vt-detailsValue">
                  <a 
                    href={file.secureUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="vt-fileLink"
                  >
                    Open in new tab →
                  </a>
                </dd>
              </div>
            )}
          </dl>

          {customer && file.type === 'UTILITY_BILL' && (
            <div className="vt-ocrSection">
              <h3 className="vt-ocrTitle">Extracted Information</h3>
              <div className="vt-ocrBlock">
                <h4 className="vt-ocrLabel">Address</h4>
                <div className="vt-ocrText">{customer.address || '—'}</div>
              </div>
              <div className="vt-ocrBlock">
                <h4 className="vt-ocrLabel">Country Code</h4>
                <div className="vt-ocrText">{customer.addressCountryCode || '—'}</div>
              </div>
              {customer.utilityBillConfidence !== null && customer.utilityBillConfidence !== undefined && (
                <div className="vt-ocrBlock">
                  <h4 className="vt-ocrLabel">Confidence Score</h4>
                  <div className="vt-ocrText">
                    {customer.utilityBillConfidence.toFixed(1)}%
                    {customer.utilityBillConfidence >= 80 && (
                      <span style={{ marginLeft: '0.5rem', color: '#10b981' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {customer && file.type === 'PASSPORT' && (
            <div className="vt-ocrSection">
              <h3 className="vt-ocrTitle">Extracted Information</h3>
              <div className="vt-ocrBlock">
                <h4 className="vt-ocrLabel">Passport Number</h4>
                <div className="vt-ocrText" style={{ fontFamily: 'monospace' }}>{customer.passportNumber || '—'}</div>
              </div>
              <div className="vt-ocrBlock">
                <h4 className="vt-ocrLabel">First Name</h4>
                <div className="vt-ocrText">{customer.passportFirstname || '—'}</div>
              </div>
              <div className="vt-ocrBlock">
                <h4 className="vt-ocrLabel">Surname</h4>
                <div className="vt-ocrText">{customer.passportSurname || '—'}</div>
              </div>
              <div className="vt-ocrBlock">
                <h4 className="vt-ocrLabel">Date of Birth</h4>
                <div className="vt-ocrText">
                  {customer.passportDateOfBirth 
                    ? new Date(customer.passportDateOfBirth).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    : '—'}
                </div>
              </div>
              {customer.passportConfidence !== null && customer.passportConfidence !== undefined && (
                <div className="vt-ocrBlock">
                  <h4 className="vt-ocrLabel">Confidence Score</h4>
                  <div className="vt-ocrText">
                    {customer.passportConfidence.toFixed(1)}%
                    {customer.passportConfidence >= 80 && (
                      <span style={{ marginLeft: '0.5rem', color: '#10b981' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {(!customer || (file.type !== 'UTILITY_BILL' && file.type !== 'PASSPORT')) && (
            <div className="vt-ocrSection">
              <p className="vt-muted">No extracted information available for this file type</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
